import { InlineDatePicker } from 'components/form-elements/pickers/InlineDatePicker';
import React, { useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { daysFormat } from 'utils/timeAndDate';
import { useLaundrySchedules } from 'services/laundry/schedules';
import { ShouldRender } from 'components/ShouldRender';
import { Text } from 'components/typography';
import Loader from 'components/loaders/Loader';
import { isBefore } from 'date-fns';
import { useNonBookableSlots } from 'services/laundry/non-bookable-slots';
import { useModal } from 'store/modals';
import { LaundryBrikkModalContext } from '../../index';
import { ResourceSelectionSection } from './components/ResourceSelectionSection';
import styles from './styles.module.scss';

export function getDateFromNow(daysFromNow) {
  const now = new Date();
  const result = new Date(now);
  result.setTime(result.getTime() + daysFromNow * 24 * 60 * 60 * 1000);
  result.setMilliseconds(0);
  return result;
}

export const DateSelectionSection = ({ laundryResources }) => {
  const {
    selectedDate,
    selectedEndDate,
    setSelectedDate,
    setSelectedEndDate,
    selectedResource,
  } = useContext(LaundryBrikkModalContext);
  const { laundrySchedules, isLoading } = useLaundrySchedules(
    selectedResource?.id,
  );
  const [error, setError] = useState('');
  const maxDate = getDateFromNow(laundrySchedules?.bookingRule?.notBefore);
  const minDate = getDateFromNow(laundrySchedules?.bookingRule?.notAfter);
  const minDuration = laundrySchedules?.bookingRule?.minDuration;
  const maxDuration = laundrySchedules?.bookingRule?.maxDuration;
  const [tempEndDate, setTempEndDate] = useState(selectedEndDate);
  const [tempStartDate, setTempStartDate] = useState(null);
  const intl = useIntl();

  const { openModal } = useModal();

  const handleRangeChange = (dates) => {
    const [start, end] = dates;
    setSelectedDate(start);
    setTempStartDate(start);
    setTempEndDate(end);

    if (start && end) {
      const diffInDays = Math.floor((end - start) / (1000 * 60 * 60 * 24));
      if (diffInDays < minDuration || diffInDays > maxDuration) {
        openModal({
          modalType: 'NotificationModal',
          modalProps: {
            type: 'error',
            description: 'selectDurationBetween',
            intlValues: { minDuration, maxDuration },
          },
        });
        return;
      }
    }

    setError('');
    setSelectedEndDate(end);
  };

  const handleDateChange = (selectedDate) => setSelectedDate(!selectedDate ? minDate : selectedDate);

  const { nonBookableSlots } = useNonBookableSlots(selectedResource?.id);

  // Build metadata for dates to show information on hover
  const dateMetadata = React.useMemo(() => {
    const metadata = new Map();

    if (!nonBookableSlots || !Array.isArray(nonBookableSlots) || !minDate) {
      return metadata;
    }

    // Get minDate at midnight for comparison
    const minDateTime = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate(), 0, 0, 0, 0);

    // Create a map of all dates in the list for quick lookup
    const dateMap = new Map();
    nonBookableSlots.forEach(slot => {
      if (slot?.date) {
        const slotDate = new Date(slot.date);
        const normalizedDate = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate());
        dateMap.set(normalizedDate.getTime(), slot);
      }
    });

    // Mark all checkout dates (check-in not available) - only for dates >= minDate
    nonBookableSlots.forEach(bookingDate => {
      const isCheckoutOnly = bookingDate?.isStartDate;

      if (bookingDate?.date && isCheckoutOnly) {

        const booking = new Date(bookingDate?.date);

        if (!Number.isNaN(booking?.getTime())) {
          const normalizedDate = new Date(Date.UTC(
            booking?.getUTCFullYear(),
            booking?.getUTCMonth(),
            booking?.getUTCDate(),
            0, 0, 0, 0,
          ));
          const localDate = new Date(
            normalizedDate.getUTCFullYear(),
            normalizedDate.getUTCMonth(),
            normalizedDate.getUTCDate(),
            0, 0, 0, 0,
          );

          // Check if previous date exists in the list
          const previousDate = new Date(localDate);
          previousDate.setDate(localDate.getDate() - 1);
          const hasPreviousDate = dateMap.has(previousDate.getTime());

          // Only add metadata if the date is >= minDate and doesn't have previous date in list
          if (localDate >= minDateTime && isCheckoutOnly && !hasPreviousDate) {
            metadata.set(localDate.getTime(), {
              type: 'checkout',
              message: intl.formatMessage({ id: 'userAlreadyCheckedIn' }),
            });
          }
        }
      }
    });

    return metadata;
  }, [nonBookableSlots, minDate]);

  const getDayClassName = React.useCallback((booking) => {

    const timestamp = new Date(booking).setHours(0, 0, 0, 0);
    const meta = dateMetadata.get(timestamp);

    if (!meta) return '';

    return 'checkout-date';
  }, [dateMetadata]);

  const renderDayContents = React.useCallback((day, date) => {
    const timestamp = new Date(date).setHours(0, 0, 0, 0);
    const meta = dateMetadata.get(timestamp);

    if (meta) {
      return (
        <span
          title={meta.message}
          className={styles.dateWithInfo}
        >
          {day}
        </span>
      );
    }

    return day;
  }, [dateMetadata]);

  return (
    <div className={styles.wrapper}>
      <ResourceSelectionSection laundryResources={laundryResources} />
      <ShouldRender condition={isLoading}>
        <Loader />
      </ShouldRender>
      <ShouldRender condition={laundrySchedules?.scheduleType === 'slots'}>
        <div>
          <InlineDatePicker
            isInline
            selectedDate={isBefore(selectedDate, minDate) ? minDate : selectedDate}
            minDate={minDate}
            maxDate={maxDate}
            dateFormat={daysFormat}
            onChange={handleDateChange}
          />
        </div>
      </ShouldRender>
      <ShouldRender condition={laundrySchedules?.scheduleType === 'daily'}>
        <div>
          <InlineDatePicker
            isInline
            selectsRange
            selectedDate={isBefore(selectedDate, minDate) ? minDate : selectedDate}
            startDate={tempStartDate}
            endDate={tempEndDate}
            dateFormat={daysFormat}
            onChange={handleRangeChange}
            minDate={minDate}
            maxDate={maxDate}
            dayClassName={getDayClassName}
            renderDayContents={renderDayContents}
            excludeDates={React.useMemo(() => {
              if (!nonBookableSlots || !Array.isArray(nonBookableSlots)) return [];

              const datesToExclude = [];

              // Create a map of all dates in the list for quick lookup
              const dateMap = new Map();
              nonBookableSlots.forEach(slot => {
                if (slot?.date) {
                  const slotDate = new Date(slot.date);
                  const normalizedDate = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate());
                  dateMap.set(normalizedDate.getTime(), slot);
                }
              });

              // Exclude dates where isStartDate is false
              nonBookableSlots.forEach(slot => {
                if (slot?.isStartDate === false && slot?.date) {
                  datesToExclude.push(new Date(slot.date));
                }
              });

              // Exclude current date if isStartDate is true and the previous date exists in the list
              nonBookableSlots.forEach(slot => {
                if (slot?.isStartDate === true && slot?.date) {
                  const currentDate = new Date(slot.date);
                  const normalizedCurrentDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
                  const previousDate = new Date(normalizedCurrentDate);
                  previousDate.setDate(previousDate.getDate() - 1);

                  // Check if previous date exists in the list
                  if (dateMap.has(previousDate.getTime())) {
                    datesToExclude.push(new Date(slot.date));
                  }
                }
              });

              return datesToExclude;
            }, [nonBookableSlots])}
          />
          <div className={styles.legend}>
            <div className={styles.legendItem}>
              <span className={styles.dotCheckout} />
              <span className={styles.legendText}>
                <FormattedMessage
                  id="availableForCheckoutOnly"
                  defaultMessage="Available for checkout only"
                />
              </span>
            </div>
          </div>
          <Text
            red
            bold
          >
            {error}
          </Text>
        </div>
      </ShouldRender>
    </div>
  );
};
